import ThemeModal from "components/Common/ThemeModal"
import { Badge, Button, Col, Input, Row, Spinner } from "reactstrap"
import { useState } from "react"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { getEntityLabel, getUserLabel } from "pages/AccountPages/utils/displayValue"

const getMoneyDisplayValue = (value, currency = "BDT") => formatCurrency(value || 0, currency)

const ReceiptRow = ({ label, value, mono = false, strong = false }) => (
  <div className="d-flex align-items-start justify-content-between gap-3 py-2" style={{ borderBottom: "1px dashed rgba(15, 23, 42, 0.12)" }}>
    <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1 }}>{label}</span>
    <span className={`${mono ? "font-monospace" : ""} ${strong ? "fw-bold" : "fw-semibold"} text-dark text-end`.trim()} style={{ fontSize: strong ? 15 : 14 }}>
      {value || "-"}
    </span>
  </div>
)

const ReceiptSection = ({ title, children }) => (
  <div className="pt-3">
    <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>{title}</div>
    {children}
  </div>
)

const AccountExpenseView = ({ modal, toggleModal, expense, loading, onApprove, onReject }) => {
  const [rejectionReason, setRejectionReason] = useState("")
  const [showRejectInput, setShowRejectInput] = useState(false)

  const typeName = getEntityLabel(expense?.expense_type || expense?.expenseType, expense?.expense_type_id, "Expense Type")
  const accountName = getEntityLabel(expense?.account, expense?.account_id, "Account")
  const approvedBy = getUserLabel(expense?.approvedBy || expense?.approved_by)
  const createdBy = getUserLabel(expense?.createdBy || expense?.created_by)
  const updatedBy = getUserLabel(expense?.updatedBy || expense?.updated_by)

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title="Expense Details" size="xl" modalBodyClass="bg-body-tertiary">
      {loading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : !expense ? (
        <div className="text-center py-4 text-muted">No details available.</div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: 740 }}>
          <div className="rounded-4 border overflow-hidden" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)", borderColor: "rgba(15, 23, 42, 0.08)", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)" }}>

            <div className="px-4 py-2 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(90deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.96) 100%)" }}>
              <div className="text-white text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1.6 }}>Expense Record</div>
              <div className="text-white-50 font-monospace" style={{ fontSize: 12 }}>AEX-{String(expense.id ?? "0000").padStart(4, "0")}</div>
            </div>

            {expense.is_reversed && (
              <div className="px-4 py-2 d-flex align-items-center gap-2" style={{ background: "rgba(100, 116, 139, 0.12)", borderBottom: "1px solid rgba(100, 116, 139, 0.2)" }}>
                <i className="mdi mdi-undo-variant text-secondary" style={{ fontSize: 16 }} />
                <span className="text-secondary fw-semibold small">This expense has been reversed in the ledger — it is no longer counted in balances or totals.</span>
              </div>
            )}

            <div className="px-4 py-3 border-bottom" style={{ background: "radial-gradient(circle at top left, rgba(239, 68, 68, 0.1), transparent 34%), linear-gradient(180deg, #f8fbfd 0%, #ffffff 100%)", borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Expense type</div>
                  <div className="h5 mb-1 text-dark" style={{ letterSpacing: -0.2 }}>{typeName}</div>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge color="danger" pill className="px-2 py-1">Debit</Badge>
                    {expense.is_reversed && <Badge color="secondary" pill className="px-2 py-1">Reversed</Badge>}
                    <span className="text-muted small font-monospace">#{expense.id ?? "-"}</span>
                    <span className="text-muted small">{expense.expense_date ? formatReadableDate(expense.expense_date) : "No date"}</span>
                  </div>
                </div>
                <div className="text-md-end">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Amount</div>
                  <div className="fw-bold text-dark" style={{ fontSize: 34, lineHeight: 0.95, letterSpacing: -1 }}>{getMoneyDisplayValue(expense.amount)}</div>
                  <div className="text-muted small mt-1">from {accountName}</div>
                </div>
              </div>
            </div>

            <div className="position-relative" style={{ height: 18, background: "#fff" }}>
              <div style={{ position: "absolute", left: 0, right: 0, top: "50%", borderTop: "1px dashed rgba(15, 23, 42, 0.18)" }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, left: -11, top: -2 }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, right: -11, top: -2 }} />
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <Row className="g-3 g-md-4">
                <Col md={6}>
                  <ReceiptSection title="Expense Details">
                    <ReceiptRow label="Expense Type" value={typeName} />
                    <ReceiptRow label="Amount" value={getMoneyDisplayValue(expense.amount)} mono />
                    <ReceiptRow label="Expense Date" value={expense.expense_date ? formatReadableDate(expense.expense_date) : "-"} />
                    <ReceiptRow label="Account" value={accountName} />
                  </ReceiptSection>
                </Col>
                <Col md={6}>
                  <ReceiptSection title="Approval Info">
                    <ReceiptRow label="Status" value={expense.approval_status ?? "Pending"} />
                    <ReceiptRow label="Ledger Status" value={expense.is_reversed ? "Reversed" : "Active"} />
                    <ReceiptRow label="Approved By" value={approvedBy} />
                    <ReceiptRow label="Created By" value={createdBy} />
                    <ReceiptRow label="Record ID" value={expense.id ?? "-"} mono />
                    <ReceiptRow label="Debit Account" value={accountName} />
                  </ReceiptSection>
                  {(onApprove || onReject) && expense.approval_status !== "approved" && expense.approval_status !== "rejected" && (
                    <div className="mt-3 d-flex flex-column gap-2">
                      {onApprove && (
                        <Button color="success" size="sm" onClick={() => onApprove(expense.id)}>
                          <i className="mdi mdi-check-circle-outline me-1" />Approve
                        </Button>
                      )}
                      {onReject && !showRejectInput && (
                        <Button color="danger" size="sm" outline onClick={() => setShowRejectInput(true)}>
                          <i className="mdi mdi-close-circle-outline me-1" />Reject
                        </Button>
                      )}
                      {onReject && showRejectInput && (
                        <div className="d-flex flex-column gap-2">
                          <Input
                            type="textarea"
                            rows={2}
                            bsSize="sm"
                            placeholder="Rejection reason (optional)"
                            value={rejectionReason}
                            onChange={e => setRejectionReason(e.target.value)}
                          />
                          <div className="d-flex gap-2">
                            <Button color="danger" size="sm" onClick={() => { onReject(expense.id, rejectionReason); setShowRejectInput(false); setRejectionReason("") }}>
                              Confirm Reject
                            </Button>
                            <Button color="secondary" size="sm" outline onClick={() => { setShowRejectInput(false); setRejectionReason("") }}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <ReceiptSection title="Expense Ledger">
                <div className="rounded-3 px-3 py-2" style={{ background: "rgba(248, 250, 252, 0.9)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                  <ReceiptRow label="Type" value={typeName} />
                  <ReceiptRow label="Debit Account" value={accountName} />
                  <ReceiptRow label="Approved By" value={approvedBy} />
                  <ReceiptRow label="Total Expense" value={getMoneyDisplayValue(expense.amount)} strong mono />
                </div>
              </ReceiptSection>
            </div>

            {expense.description && (
              <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
                <ReceiptSection title="Description">
                  <div className="text-dark rounded-3 px-3 py-2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, background: "linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 1) 100%)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                    {expense.description}
                  </div>
                </ReceiptSection>
              </div>
            )}

            <div className="px-4 py-3" style={{ background: "linear-gradient(180deg, rgba(248, 250, 252, 0.75) 0%, rgba(241, 245, 249, 0.9) 100%)" }}>
              <Row className="g-3">
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Created</div>
                  <div className="small text-dark">{createdBy}</div>
                  <div className="small text-muted">{expense.created_at ? formatReadableDate(expense.created_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Updated</div>
                  <div className="small text-dark">{updatedBy}</div>
                  <div className="small text-muted">{expense.updated_at ? formatReadableDate(expense.updated_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Status</div>
                  <div className="small text-dark fw-semibold">{expense.deleted_at ? "Archived" : "Active"}</div>
                  <div className="small text-muted">{expense.deleted_at ? formatReadableDate(expense.deleted_at) : "No deletion recorded"}</div>
                </Col>
              </Row>
            </div>

          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default AccountExpenseView
